import type { FC, MouseEventHandler } from 'react';

/**
 * @description Props for the Button component
 * @typedef {Object} ButtonProps
 */
type ButtonProps = {
  /** The type of the button element */
  type?: 'button' | 'submit';
  /** The default text to display on the button */
  defaultLabel: string;
  /** The text to display when the button is in loading state */
  loadingLabel?: string;
  /** Whether the button is in loading state */
  loading?: boolean;
  /** Whether the button is disabled */
  disabled?: boolean;
  /** Click handler for the button */
  clicked?: MouseEventHandler<HTMLButtonElement>;
};

/**
 * @description A reusable button component with loading state support.
 * Displays a spinner and alternative text during loading state.
 *
 * @SADtrace SAD-003-1.1, SAD-003-2.1, SAD-003-3.1, SAD-003-4.1, SAD-003-5.1, SAD-003-6.1
 *
 * @History
 * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
 * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
 *
 * @param {Object} props - The component props
 * @param {string} props.defaultLabel - The default text to display on the button
 * @param {string} [props.loadingLabel] - The text to display when the button is in loading state
 * @param {MouseEventHandler<HTMLButtonElement>} [props.clicked] - Click handler for the button
 * @param {boolean} [props.loading=false] - Whether the button is in loading state
 * @param {boolean} [props.disabled=false] - Whether the button is disabled
 * @param {'button'|'submit'} [props.type='button'] - The type of the button element
 *
 * @returns {JSX.Element} The Button component
 */
const Button: FC<ButtonProps> = ({
  defaultLabel,
  loadingLabel,
  clicked,
  loading = false,
  disabled = false,
  type = 'button',
  ...rest
}) => {
  return (
    <button
      onClick={clicked}
      className="btn btn-lg rounded btn-primary me-3"
      disabled={disabled}
      type={type}
      {...rest}
    >
      {loading && (
        <span
          className="spinner-border spinner-border-sm me-2"
          role="status"
          aria-hidden="true"
        ></span>
      )}
      {loading ? loadingLabel : defaultLabel}
    </button>
  );
};
export default Button;
